import { markSSOLoginPending } from "./ssoLinkTracking";

/**
 * Shared helpers for the two places that start the real OIDC/SSO round trip
 * (ADR-0037 §UX): `routes/LoginPage.tsx`'s org-slug step and
 * `routes/SSORedirectPage.tsx`'s distributed-deep-link handler. Both must
 * build the SAME `/t/{slug}/sso/login` URL and mark the SAME pending flag —
 * kept in one place so they can't drift apart.
 */

// A conservative, permissive-enough shape check: short, URL-path-safe,
// letters/digits/hyphen/underscore/dot, no leading/trailing separator. This
// is UX only (catches an obviously-mistyped slug before a wasted round trip)
// — the server is the authority on whether the tenant/slug actually exists,
// and refuses non-leakily either way (ADR-0037 §4).
const ORG_SLUG_PATTERN = /^[a-zA-Z0-9](?:[a-zA-Z0-9._-]{0,62}[a-zA-Z0-9])?$/;

export function isValidOrgSlug(raw: string): boolean {
  return ORG_SLUG_PATTERN.test(raw.trim());
}

/** The server's real, tenant-qualified login route (ssosvc.LoginHandler,
 * `GET /t/{tenant}/sso/login`) — a plain top-level navigation target, never
 * fetched via the API client (it's an OAuth redirect, not an XHR). */
export function ssoLoginPath(slug: string): string {
  return `/t/${encodeURIComponent(slug.trim())}/sso/login`;
}

/** Marks the one-time first-login-interstitial pending flag and navigates the
 * WHOLE BROWSER (not the SPA router) to the tenant's real SSO login route.
 * This is a real page unload — nothing after this call runs. */
export function beginSSOLogin(slug: string): void {
  markSSOLoginPending();
  window.location.href = ssoLoginPath(slug);
}
